import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  KeyboardEvent,
  useRef,
  MutableRefObject,
  ChangeEvent,
} from 'react';
import debounce from 'lodash-es/debounce';
import format from 'date-fns/format';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  PermissionType,
  QueryMode,
  ServiceReminderTimingType,
  ServiceWhereInput,
  useGetReminderSettingsClinicServicesQuery,
  ReminderSettingsServiceFragment,
  ReminderSettingsServiceTimingFragment,
  GetReminderSettingsClinicServicesDocument,
  useGetReminderSettingsWorkflowEventSettingQuery,
  useUpdateReminderSettingsWorkflowEventSettingMutation,
  WorkflowEvent,
  ServiceOrderByWithRelationAndSearchRelevanceInput,
  SortOrder,
} from 'shared/types/graphql';
import Dropdown, { IOption, PopoverPlacement } from 'shared/components/Dropdown';
import EditRemindersModal from './EditModal';
import {
  Box,
  Flex,
  VStack,
  Collapse,
  Divider,
  Spacer,
  useDisclosure,
  Table,
  Thead,
  Tr,
  Td,
  Th,
  Tbody,
  HStack,
} from '@televet/kibble-ui/build/chakra';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import { Checkbox } from '@televet/kibble-ui/build/components/Checkbox';
import { Popover } from '@televet/kibble-ui/build/components/Popover';
import { Link } from '@televet/kibble-ui/build/components/Link';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Pagination } from '../../../../shared/components/Pagination';
import { ReminderAppointmentRequestFormLink } from './ReminderAppointmentRequestFormLink';
import { formatPluralization, scrollIntoView } from 'shared/utils';
import DevModeDisplayJSON from '../../components/DevModeDisplayJSON';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import { FeatureFlagName, GraphQLErrorPolicies, GraphQLFetchPolicies, Placement } from 'shared/enums';
import { Link as ReactRouterLink } from 'react-router-dom';
import ChannelStatusSelect from 'shared/components/ChannelStatusSelect/ChannelStatusSelect';
import ServiceFilterButton from './ServiceFilterButton';
import { useApolloClient } from '@apollo/client';
import { comparator } from './utils/timingComparator';
import { useServiceReminders } from './useServiceReminders';
import EnableRemindersModal from './EnableRemindersModal';
import { TableColumnSort, TableColumnSortOrder } from 'shared/components/TableColumnSort';
import { ReminderEmailDeliveryTemplateEdit } from './ReminderEmailDeliveryTemplateEdit';
import HasFeature from 'shared/components/HasFeature';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { Status } from '@televet/kibble-ui/build/shared/types';

enum ReminderSettingsMenuOption {
  SelectCurrentPage = 'SelectCurrentPage',
  SelectAllPages = 'SelectAllPages',
  ClearAll = 'ClearAll',
}

enum ReminderStatusLabel {
  Inactive = 'Inactive',
  Scheduled = 'Scheduled',
  Active = 'Active',
}

const defaultOrderBy: ServiceOrderByWithRelationAndSearchRelevanceInput = { name: SortOrder.Asc };

const DefaultAutomatedResponse =
  'Thanks for contacting us to schedule your appointment. Our team will reach out shortly with availability and assist with booking your appointment.';

const editRemindersOptions = [
  {
    text: 'Select This Page',
    value: ReminderSettingsMenuOption.SelectCurrentPage,
  },
  {
    text: 'Select All Pages',
    value: ReminderSettingsMenuOption.SelectAllPages,
  },
  {
    text: 'Clear All Selected',
    value: ReminderSettingsMenuOption.ClearAll,
  },
];

interface props {
  viewRef: MutableRefObject<HTMLDivElement | null>;
}

const RemindersSection = ({ viewRef }: props): JSX.Element => {
  const { isOpen, onToggle } = useDisclosure();
  const { userHasPermission, currentClinicId } = useClinicUser();
  const { primaryIntegrationName, isPostcardsSupported, isBillingCodeSupported } = useIntegrationsProvider();

  const [hideServicesWithoutReminders, setHideServicesWithoutReminders] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = useState<number>(10);
  const [orderBy, setOrderBy] = useState<ServiceOrderByWithRelationAndSearchRelevanceInput>(defaultOrderBy);
  const [billingCodeOrder, setBillingCodeOrder] = useState<TableColumnSortOrder>();
  const [displayNameOrder, setDisplayNameOrder] = useState<TableColumnSortOrder>(SortOrder.Asc);
  const [selectedService, setSelectedService] = useState<ReminderSettingsServiceFragment>();
  const [selectedServices, setSelectedServices] = useState<ReminderSettingsServiceFragment[]>([]);
  const [allRemindersSelected, setAllRemindersSelected] = useState<boolean>(false);
  const [excludedServicesId, setExcludedServicesId] = useState<string[]>([]); // Holds a list of elements that got excluded when allRemindersSelected = true
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const client = useApolloClient();
  const tableRef = useRef<HTMLTableElement | null>(null);
  const { toastMessagePop } = useCustomToast();
  const [filter, setFilter] = useState<ServiceWhereInput>();

  const selectedRemindersIds = useMemo(() => selectedServices.map((service) => service.id), [selectedServices]);
  const { clinicHasServiceReminders } = useServiceReminders(currentClinicId);
  const {
    isOpen: isConfirmationModalOpen,
    onOpen: openConfirmationModal,
    onClose: closeConfirmationModal,
  } = useDisclosure();

  const canEdit = userHasPermission(PermissionType.WriteClinic);

  const queryServicesWhere = useMemo((): ServiceWhereInput => {
    const where = {
      OR: [
        {
          code: { contains: debouncedSearchText, mode: QueryMode.Insensitive },
        },
        {
          name: { contains: debouncedSearchText, mode: QueryMode.Insensitive },
        },
      ],
      integration: {
        clinicId: { equals: currentClinicId },
      },
      isDeleted: { equals: false },
      hasConfiguredReminders: hideServicesWithoutReminders ? { equals: true } : undefined,
    };
    return !!filter?.AND?.length ? { ...where, ...filter } : where;
  }, [currentClinicId, debouncedSearchText, filter, hideServicesWithoutReminders]);

  const {
    data: remindersData,
    previousData: previousRemindersData,
    loading,
    refetch,
  } = useGetReminderSettingsClinicServicesQuery({
    variables: {
      where: queryServicesWhere,
      orderBy: orderBy,
      take: currentPageSize,
      skip: currentPageSize * (currentPage - 1),
    },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const services = useMemo(
    () => remindersData?.findManyService || previousRemindersData?.findManyService,
    [previousRemindersData?.findManyService, remindersData?.findManyService],
  );

  const servicesCount = useMemo(
    () => remindersData?.findManyServiceCount || previousRemindersData?.findManyServiceCount,
    [previousRemindersData?.findManyServiceCount, remindersData?.findManyServiceCount],
  );

  const { data: workflowData } = useGetReminderSettingsWorkflowEventSettingQuery({
    variables: {
      where: {
        clinicId: { equals: currentClinicId },
        event: { equals: WorkflowEvent.ServiceReminderFormSubmitted },
        deletedAt: { equals: null },
      },
    },
  });

  const [updateWorkflowEventSetting, { loading: updatingWorkflowEventSetting }] =
    useUpdateReminderSettingsWorkflowEventSettingMutation({
      errorPolicy: GraphQLErrorPolicies.All,
    });

  const handleSearchInputKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      setSearchText('');
    }
  }, []);

  // eslint-disable-next-line
  const handleSearchTextChange = useCallback<((searchText: string) => void) & { flush: () => void | undefined }>(
    debounce(
      (searchText): void => {
        const text = searchText.trim();
        setDebouncedSearchText(text);
        setCurrentPage(1);
      },
      340,
      { leading: false, trailing: true },
    ),
    [currentClinicId],
  );

  useEffect(() => {
    handleSearchTextChange(searchText);
  }, [searchText, handleSearchTextChange]);

  const handleCheckboxClick = (data: ReminderSettingsServiceFragment): void => {
    if (!isOpen) onToggle();

    const { id } = data;

    if (allRemindersSelected) {
      const isReminderExcluded = excludedServicesId.includes(id);

      if (isReminderExcluded) {
        setExcludedServicesId((currentExcludedIds) => [
          ...currentExcludedIds.filter((existingId) => existingId !== id),
        ]);
      } else {
        if (excludedServicesId.length + 1 === servicesCount) {
          // Means we are already excluding all services
          deselectAllReminders();
        } else {
          setExcludedServicesId((currentExcludedIds) => [...currentExcludedIds, id]);
        }
      }
    } else {
      if (selectedRemindersIds.length === 0) {
        setSelectedService(data);
      } else {
        setSelectedService(undefined);
      }

      const isReminderSelected = selectedRemindersIds.includes(id);

      if (isReminderSelected) {
        const updatedSelectedServices = [
          ...selectedServices.filter((service: ReminderSettingsServiceFragment) => service.id !== id),
        ];
        setSelectedServices(updatedSelectedServices);
        if (!updatedSelectedServices.length && isOpen) onToggle();
      } else {
        setSelectedServices((currentSelectedServices) => [...currentSelectedServices, data]);
      }
    }
  };

  const editReminders = async (): Promise<void> => {
    if (selectedReminderCount === 1) {
      const { data } = await client.query({
        query: GetReminderSettingsClinicServicesDocument,
        variables: {
          where: {
            id: {
              equals: selectedRemindersIds[0],
            },
          },
        },
      });

      setSelectedService(data.findManyService[0]);
    } else {
      setSelectedService(undefined);
    }
    setOpenEditModal(true);
  };

  const editReminder =
    (data: ReminderSettingsServiceFragment): (() => void) =>
    (): void => {
      setSelectedService(data);
      setOpenEditModal(true);
    };

  const selectCurrentPageReminders = (): void => {
    setAllRemindersSelected(false);
    setExcludedServicesId([]);
    if (services) {
      // Append all services currently loaded on this page to the selection.
      // Due to potential changes from reordering or other modifications
      // the in-memory versions of these services might not match their current state.
      // Therefore, we identify and merge services based on their unique 'id' property to ensure accuracy
      // rather than relying on object reference comparisons.

      const seenIds = new Map(selectedServices.map((service) => [service.id, service]));
      services.forEach((service) => {
        if (!seenIds.has(service.id)) {
          seenIds.set(service.id, service);
        }
      });
      setSelectedServices(Array.from(seenIds.values()));

      if (!isOpen) onToggle();
    }
  };

  const selectAllReminders = (): void => {
    setAllRemindersSelected(true);
    setSelectedServices([]);
    setExcludedServicesId([]);
    if (!isOpen) onToggle();
  };

  const deselectAllReminders = (): void => {
    setAllRemindersSelected(false);
    setSelectedServices([]);
    setExcludedServicesId([]);
    if (isOpen) onToggle();
  };

  const handleMultiSelectCheckboxClick = (): void => {
    if (allRemindersSelected || areAllRemindersSelectedManually) deselectAllReminders();
    if (areSomeRemindersSelected) selectAllReminders();
    if (areNoRemindersSelected) selectCurrentPageReminders();
    setExcludedServicesId([]);
  };

  const handleReminderSettingsMenuOptionClick = (
    _event: React.MouseEvent<Element, globalThis.MouseEvent>,
    option: IOption<ReminderSettingsMenuOption>,
  ): void => {
    const { value } = option;

    if (value === ReminderSettingsMenuOption.SelectAllPages) {
      selectAllReminders();
    } else if (value === ReminderSettingsMenuOption.SelectCurrentPage) {
      selectCurrentPageReminders();
    } else if (value === ReminderSettingsMenuOption.ClearAll) {
      deselectAllReminders();
    }
  };

  const handleCloseModal = (): void => {
    setOpenEditModal(false);
    setSelectedService(undefined);
  };

  const handleReminderSave = (): void => {
    handleCloseModal();
    deselectAllReminders();
    refetch();
  };

  const handleHideServicesWithoutRemindersToggle = (): void => {
    setHideServicesWithoutReminders((value) => !value);
    setCurrentPage(1);
  };

  const handlePageChange = useCallback(
    (page: number): void => {
      setCurrentPage(page);
      if (!loading) {
        if (viewRef.current && tableRef.current) scrollIntoView(viewRef.current, tableRef.current, 500);
      }
    },
    [loading, viewRef],
  );

  const handlePageSize = useCallback(
    (pageSize: number) => {
      handlePageChange(1);
      setCurrentPageSize(pageSize);
    },
    [handlePageChange, setCurrentPageSize],
  );

  const updateServicesWhere = useMemo((): ServiceWhereInput => {
    if (allRemindersSelected) {
      return {
        ...queryServicesWhere,
        id: {
          notIn: excludedServicesId,
        },
      };
    }

    if (!allRemindersSelected && !!selectedService) {
      return {
        id: { equals: selectedService?.id },
      };
    }

    return {
      id: { in: selectedRemindersIds },
    };
  }, [allRemindersSelected, selectedService, selectedRemindersIds, queryServicesWhere, excludedServicesId]);

  const noResults = useMemo(() => {
    return !loading && !servicesCount;
  }, [loading, servicesCount]);

  const areAllRemindersSelectedManually = useMemo(() => {
    return !allRemindersSelected && selectedRemindersIds.length === servicesCount;
  }, [allRemindersSelected, selectedRemindersIds, servicesCount]);

  const areSomeRemindersSelected = useMemo(() => {
    if (allRemindersSelected && excludedServicesId.length > 0) {
      return true;
    }

    return selectedRemindersIds.length > 0 && !areAllRemindersSelectedManually;
  }, [allRemindersSelected, areAllRemindersSelectedManually, excludedServicesId.length, selectedRemindersIds.length]);

  const areNoRemindersSelected = useMemo(() => {
    return !allRemindersSelected && selectedRemindersIds.length === 0;
  }, [allRemindersSelected, selectedRemindersIds]);

  const buildTimingColumn = (reminder: ReminderSettingsServiceFragment): JSX.Element => {
    const count = reminder.reminderTimings.length;

    function getTimeDisplayFromTiming(timing: ReminderSettingsServiceTimingFragment): string {
      return timing.hourOfDay > 11
        ? timing.hourOfDay === 12
          ? '12'
          : timing.hourOfDay - 12 + 'pm'
        : timing.hourOfDay + 'am';
    }

    const sorted = [...reminder.reminderTimings].sort(comparator);

    return (
      <Text>
        {count}
        {formatPluralization({ word: ' Reminder', singularCriteria: count })}
        {count > 0 && (
          <Popover
            headerProps={{ children: 'Reminders' }}
            {...(canEdit && {
              footerProps: {
                hideCancelButton: true,
                submitText: 'Edit',
                onSubmit: editReminder(reminder),
              },
            })}
            anchor={
              <Button size="xs" variant="ghost">
                Show
              </Button>
            }
            placement="bottom-start"
            closeOnBlur={true}
            isLazy={true}
          >
            {sorted.map((timing) => {
              return (
                <Text variant="onContrast" key={timing.id} size="sm">
                  {timing.offset}{' '}
                  {formatPluralization({
                    word: timing.offsetUnit.toLowerCase().substring(0, timing.offsetUnit.length - 1),
                    singularCriteria: timing.offset,
                  })}{' '}
                  {timing.timingType === ServiceReminderTimingType.BeforeDue ? 'before' : 'after'}
                  {' at '}
                  {getTimeDisplayFromTiming(timing)}
                </Text>
              );
            })}
          </Popover>
        )}
      </Text>
    );
  };

  const selectedReminderCount = useMemo(
    () => (allRemindersSelected ? (servicesCount || 0) - excludedServicesId.length : selectedRemindersIds.length),
    [allRemindersSelected, selectedRemindersIds.length, servicesCount, excludedServicesId.length],
  );

  const [automatedResponse, setAutomatedResponse] = useState('');

  const workflowEventAction = useMemo(() => {
    if (!workflowData?.findManyWorkflowEventSetting?.length) {
      return;
    }
    const action = workflowData?.findManyWorkflowEventSetting[0].actions[0];
    if (action) {
      setAutomatedResponse(action.config.messageBodyTemplate);
    }
    return action;
  }, [workflowData]);

  const resetAutomatedResponse = (): void => {
    setAutomatedResponse(DefaultAutomatedResponse);
  };

  const handleStatusSelect = async (channelStatusId: string): Promise<void> => {
    try {
      await updateWorkflowEventSetting({
        variables: {
          where: {
            id: workflowData?.findManyWorkflowEventSetting?.length
              ? workflowData?.findManyWorkflowEventSetting[0].id
              : '',
          },
          data: {
            actions: {
              update: [
                {
                  where: {
                    id: workflowEventAction?.id,
                  },
                  data: {
                    channelStatusChange: { connect: { id: channelStatusId } },
                  },
                },
              ],
            },
          },
        },
      });
      toastMessagePop('Conversation status successfully updated.');
    } catch (e) {
      console.error(e);
      toastMessagePop('Something went wrong changing the conversation status for service reminders.', STATUSES.error);
    }
  };

  const handleAutomatedResponseChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setAutomatedResponse(e.target.value);
  };

  const automatedResponseHasChanges = useMemo(() => {
    return automatedResponse !== workflowEventAction?.config.messageBodyTemplate;
  }, [automatedResponse, workflowEventAction]);

  const handleSaveAutomatedResponse = async (): Promise<void> => {
    const config = {
      messageBodyTemplate: automatedResponse,
    };
    try {
      await updateWorkflowEventSetting({
        variables: {
          where: {
            id: workflowData?.findManyWorkflowEventSetting?.length
              ? workflowData?.findManyWorkflowEventSetting[0].id
              : '',
          },
          data: {
            actions: {
              update: [
                {
                  where: {
                    id: workflowEventAction?.id,
                  },
                  data: {
                    config: config,
                  },
                },
              ],
            },
          },
        },
      });
      toastMessagePop('Automated response successfully updated.');
    } catch (e) {
      console.error(e);
      toastMessagePop('Something went wrong changing the automated response for service reminders.', STATUSES.error);
    }
  };

  const handleCancelAutomatedResponse = (): void => {
    setAutomatedResponse(workflowEventAction?.config.messageBodyTemplate);
  };

  const handleFilterChange = useCallback((filter: ServiceWhereInput): void => {
    setFilter(filter);
  }, []);

  const getServiceStateTag = (reminderActivationDate: Date): JSX.Element => {
    let variant: Status = 'default';
    let label = ReminderStatusLabel.Inactive;

    if (!!reminderActivationDate) {
      const reminderActivationTime = !!reminderActivationDate && new Date(reminderActivationDate).getTime();
      if (reminderActivationTime > Date.now()) {
        variant = 'warning';
        label = ReminderStatusLabel.Scheduled;
      } else {
        variant = 'success';
        label = ReminderStatusLabel.Active;
      }
    }

    const tag = <Tag size="sm" variant={variant} my={0.5} label={label} />;

    if (label === ReminderStatusLabel.Scheduled) {
      return (
        <Tooltip label={format(new Date(reminderActivationDate), 'M/d/yy')} placement="bottom">
          {tag}
        </Tooltip>
      );
    }

    return tag;
  };

  const defaultColumnProps = { px: 2 };
  const checkboxColumnProps = { pl: 2, pr: 0 };
  const statusColumnProps = { w: '90px' };

  const handleBillingCodeSort = (order: TableColumnSortOrder): void => {
    setDisplayNameOrder(undefined);
    setBillingCodeOrder(order);
    const sort =
      typeof order === 'undefined'
        ? defaultOrderBy
        : ({ code: order } as ServiceOrderByWithRelationAndSearchRelevanceInput);
    setOrderBy(sort);
  };

  const handleDisplayNameSort = (order: TableColumnSortOrder): void => {
    setBillingCodeOrder(undefined);
    setDisplayNameOrder(order);
    setOrderBy(
      typeof order === 'undefined'
        ? defaultOrderBy
        : ({ name: order } as ServiceOrderByWithRelationAndSearchRelevanceInput),
    );
  };

  const isChecked = (id: string): boolean => {
    if (allRemindersSelected) {
      if (excludedServicesId.includes(id)) {
        return false;
      }
      return true;
    }
    return selectedRemindersIds.includes(id);
  };

  return (
    <Box>
      <VStack spacing={8} my={8} align="left">
        <ReminderEmailDeliveryTemplateEdit />
        <ReminderAppointmentRequestFormLink clinicId={currentClinicId} />
      </VStack>

      <Box mb={8} maxWidth="500px">
        <Heading size="xs" mb={1}>
          Automated Response
        </Heading>
        <Text size="sm" variant="subtle" as="p" mb={3}>
          This response is sent to clients after they fill out the form.
        </Text>
        <Textarea
          bgColor="background.default"
          size="sm"
          value={automatedResponse}
          onChange={handleAutomatedResponseChange}
          isDisabled={!canEdit}
          mt={3}
        />
        {canEdit && (
          <Button size="sm" onClick={resetAutomatedResponse} variant="ghost" mt={1}>
            Reset to Default
          </Button>
        )}
      </Box>

      <Box mb={8}>
        <Heading size="xs" mb={1}>
          Conversation Status
        </Heading>
        <Box mb={3}>
          <Text variant="subtle" size="sm" as="p" maxWidth="500px">
            This status is assigned to the conversation after a client fills out the appointment request form.
            Conversation statuses can be viewed and edited in{' '}
            <Link size="sm" as={ReactRouterLink} to="/settings/clinic/conversations">
              Conversations Settings
            </Link>
            .
          </Text>
        </Box>
        <ChannelStatusSelect
          currentStatus={workflowEventAction?.channelStatusChange || undefined}
          placement={Placement.BottomStart}
          onStatusSelect={handleStatusSelect}
          loading={updatingWorkflowEventSetting}
          style="dropdown"
          isDisabled={!canEdit}
        />
      </Box>

      <Divider my={5} />

      <Collapse in={!clinicHasServiceReminders} animateOpacity>
        <Box py={5}>
          <Alert
            status="warning"
            title="Service reminders are disabled"
            hideCloseButton
            buttonProps={{
              isDisabled: !canEdit,
              onClick: openConfirmationModal,
              variant: 'secondary',
              size: 'xs',
              buttonText: 'Enable Reminders',
            }}
          />
        </Box>
        <EnableRemindersModal isOpen={isConfirmationModalOpen} onClose={closeConfirmationModal} />
      </Collapse>

      <HStack my={5} justify="space-between">
        <HStack justify="flex-start" align="flex-start" spacing={2}>
          <TextInput
            placeholder="Search code or name..."
            value={searchText}
            onChange={(e): void => setSearchText(e.target.value)}
            onKeyDown={handleSearchInputKeyDown}
            data-mixpanel-name="Search reminders input"
            data-testid="search-reminders-input"
            isClearable
            leftElement={<Icon name="magnifyingGlass" size="md" />}
            onClear={(): void => setSearchText('')}
            w="320px"
          />
          <ServiceFilterButton handleFilterChange={handleFilterChange} showPostcards={isPostcardsSupported} />
        </HStack>
        <Box>
          <Checkbox isChecked={hideServicesWithoutReminders} onChange={handleHideServicesWithoutRemindersToggle}>
            <Flex align="center">
              <Text>Hide services without a reminder set in {primaryIntegrationName ?? 'your PIMS'}</Text>
              <Tooltip
                label={`Uncheck this box to also see those services that don't have a reminder set in ${
                  primaryIntegrationName ?? 'your PIMS'
                }.`}
                titleCaseLabel={false}
              >
                <Flex ml="1" align="center">
                  <Icon name="infoCircle" size="xs" variant="subtle" />
                </Flex>
              </Tooltip>
            </Flex>
          </Checkbox>
        </Box>
      </HStack>

      {loading && !remindersData && !previousRemindersData ? (
        <Spinner />
      ) : (
        <>
          <Table variant="unstyled" gridColumn={'auto'} ref={tableRef}>
            <Thead>
              <Tr>
                {canEdit && (
                  <Th {...checkboxColumnProps} w="0.1%" whiteSpace="nowrap">
                    <Dropdown
                      options={editRemindersOptions}
                      onSelect={handleReminderSettingsMenuOptionClick}
                      placement={PopoverPlacement.BottomStart}
                    >
                      <Flex
                        align="center"
                        borderRadius="base"
                        px={2}
                        py={1.5}
                        _hover={{ bgColor: 'background.alpha.hover' }}
                        backgroundColor={
                          allRemindersSelected || areSomeRemindersSelected ? 'background.alpha' : 'transparent'
                        }
                        mr={2}
                        cursor="pointer"
                      >
                        <Checkbox
                          isChecked={
                            (allRemindersSelected && !excludedServicesId.length) || areAllRemindersSelectedManually
                          }
                          isIndeterminate={areSomeRemindersSelected}
                          onChange={handleMultiSelectCheckboxClick}
                          backgroundColor="background.default"
                          borderRadius="base"
                          mr={1}
                        />
                        <Icon name="chevronDown" size="sm" />
                      </Flex>
                    </Dropdown>
                  </Th>
                )}
                {isBillingCodeSupported && (
                  <Th px={0} w="0.1%" whiteSpace="nowrap" pr={2}>
                    <TableColumnSort onSort={handleBillingCodeSort} label="BILLING CODE" sortOrder={billingCodeOrder} />
                  </Th>
                )}
                <Th px={0}>
                  <TableColumnSort onSort={handleDisplayNameSort} label="DISPLAY NAME" sortOrder={displayNameOrder} />
                </Th>
                <Th {...defaultColumnProps}>
                  <Text size="xs"># OF REMINDERS</Text>
                </Th>
                <Th {...defaultColumnProps}>
                  <Text size="xs">DELIVERY</Text>
                </Th>
                <Th {...defaultColumnProps} {...statusColumnProps}>
                  <Text size="xs">STATUS</Text>
                </Th>
                <Th {...defaultColumnProps}></Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td colSpan={7} p={0}>
                  <Collapse in={isOpen} animateOpacity={true}>
                    <Flex
                      align="center"
                      justify="apart"
                      py={3}
                      pl={3}
                      pr={2}
                      backgroundColor="background.info"
                      mx={2}
                      borderRadius="lg"
                    >
                      <Text>
                        <Text fontWeight="bold">{selectedReminderCount}</Text>{' '}
                        {formatPluralization({
                          word: 'service',
                          singularCriteria: selectedReminderCount,
                        })}{' '}
                        selected
                      </Text>
                      <Spacer />
                      <Button size="sm" onClick={editReminders}>
                        Edit ({selectedReminderCount})
                      </Button>
                      <Button size="sm" variant="ghostNeutral" onClick={deselectAllReminders} iconName="close" ml={2} />
                    </Flex>
                  </Collapse>
                </Td>
              </Tr>

              <Tr>
                <Td colSpan={7} p={0}>
                  <Divider pt={3} />
                </Td>
              </Tr>

              {(remindersData || previousRemindersData)?.findManyService?.map(
                (data: ReminderSettingsServiceFragment, index) => {
                  const { id, code, reminderTimings, name, reminderActivationDate } = data;

                  return (
                    <>
                      <Tr bgColor={index % 2 ? 'background.subtle' : 'background.default'}>
                        {canEdit && (
                          <Td {...checkboxColumnProps}>
                            <Checkbox
                              onChange={(): void => handleCheckboxClick(data)}
                              isChecked={isChecked(id)}
                              ml={2}
                              mt={0}
                            />
                          </Td>
                        )}
                        {isBillingCodeSupported && (
                          <Td pl={3} w="0.1%" whiteSpace="nowrap" pr={2}>
                            <Text fontWeight="bold">{code}</Text>
                          </Td>
                        )}
                        <Td pl={3} pr={2}>
                          <Text>{name}</Text>
                        </Td>

                        <Td {...defaultColumnProps}>{reminderTimings ? buildTimingColumn(data) : '—'}</Td>
                        <Td {...defaultColumnProps}>
                          <HStack spacing={1}>
                            <Tooltip label="Email">
                              <Box>
                                <Icon
                                  color={!!reminderTimings[0]?.isEmail ? 'text.default' : 'text.placeholder'}
                                  name="envelope"
                                  size="sm"
                                />
                              </Box>
                            </Tooltip>

                            <Tooltip label="Text Message">
                              <Box>
                                <Icon
                                  color={!!reminderTimings[0]?.isSms ? 'text.default' : 'text.placeholder'}
                                  name="cellphone"
                                  size="sm"
                                />
                              </Box>
                            </Tooltip>

                            <HasFeature name={FeatureFlagName.ServiceRemindersPushNotifications}>
                              <Tooltip label="App Notification">
                                <Box>
                                  <Icon
                                    color={!!reminderTimings[0]?.isPush ? 'text.default' : 'text.placeholder'}
                                    name="notification"
                                    size="sm"
                                  />
                                </Box>
                              </Tooltip>
                            </HasFeature>

                            <Tooltip label="Pet Portal">
                              <Box>
                                <Icon
                                  color={!!data.isPetPortal ? 'text.default' : 'text.placeholder'}
                                  name="logo"
                                  size="sm"
                                />
                              </Box>
                            </Tooltip>

                            {isPostcardsSupported && (
                              <Tooltip label="Postcard">
                                <Box>
                                  <Icon
                                    color={!!data.isPostcard ? 'text.default' : 'text.placeholder'}
                                    name="mailbox"
                                    size="sm"
                                  />
                                </Box>
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                        <Td {...defaultColumnProps} {...statusColumnProps}>
                          {getServiceStateTag(reminderActivationDate)}
                        </Td>
                        <Td {...defaultColumnProps}>
                          {canEdit && (
                            <Button
                              isDisabled={!!selectedReminderCount}
                              variant="ghostNeutral"
                              iconName="pen"
                              size="sm"
                              onClick={editReminder(data)}
                            />
                          )}
                        </Td>
                      </Tr>
                      <Tr>
                        <Td colSpan={7} p={0}>
                          <DevModeDisplayJSON src={data} />
                        </Td>
                      </Tr>
                    </>
                  );
                },
              )}
            </Tbody>
          </Table>

          {!noResults && (
            <Box my={5}>
              <Pagination
                recordCount={servicesCount || 0}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onPageSize={handlePageSize}
                pageSize={currentPageSize}
                isLoading={loading}
              />
            </Box>
          )}
        </>
      )}
      {openEditModal && (
        <EditRemindersModal
          whereServices={updateServicesWhere}
          selectedService={selectedService}
          selectedServices={selectedServices}
          servicesCount={!selectedService ? selectedReminderCount || 0 : 1}
          showPostcards={isPostcardsSupported}
          isOpen={openEditModal}
          onSave={handleReminderSave}
          onClose={handleCloseModal}
          integrationDataProvider={primaryIntegrationName}
          showBillingCode={isBillingCodeSupported}
        />
      )}

      {/* Temporary until settings area save bar changes are completed  */}
      <Box mt={2} />
      <Divider />
      <Box position="sticky" bottom={0} mt={2}>
        <Collapse style={{ width: '100%' }} in={automatedResponseHasChanges && !loading}>
          <Flex w="100%" py={3} px={5} bgColor="background.subtle" justify="flex-end">
            <Flex gap={3} align="center">
              <Text>Unsaved changes</Text>
              <Button width={92} variant="tertiary" onClick={handleCancelAutomatedResponse}>
                Cancel
              </Button>
              <Button width={92} onClick={handleSaveAutomatedResponse} isLoading={updatingWorkflowEventSetting}>
                Save
              </Button>
            </Flex>
          </Flex>
        </Collapse>
      </Box>
    </Box>
  );
};

export default RemindersSection;
